export default {
  en: {
    previous: 'Previous',
    loadingStatus: 'Signing in…',
    lastUsed: 'Last used',
    claimSteps: {
      step: 'Step {n}',
      claimProfile: 'Claim supplier profile',
      supplierDetails: 'Add supplier details',
      setUpAccount: 'Set up account',
    },
    claimProfile: {
      errorExpired: 'This invite has expired or been cancelled.',
      title: 'You have been invited by {orgName} to claim your supplier profile',
      li1: 'Speed up security reviews and close deals faster',
      li2: 'Know exactly where you are in the review process',
      li3: 'Never complete a questionnaire from scratch again',
      waiting:
        '{orgName} is waiting to connect with you | {orgName} and one other client is waiting to connect with you | {orgName} and {n} other clients are waiting to connect with you',
      cta: 'Get started',
    },
    orgDetails: {
      title: 'Add your organisation details',
      orgInfo: 'Organisation information',
      businessDetails: 'Business details',
      registeredAddress: 'Registered address',
      continue: 'Continue',
      validation: {
        legalNameRequired: "Enter your company's legal name",
        countryRequired: 'Select a country',
        industryRequired: 'Select an industry or sector',
        businessPhoneRequired: 'Enter a business phone number',
        websiteRequired: 'Enter a website',
        streetAddressRequired: 'Enter a street address',
        cityRequired: 'Enter a city',
        postcodeRequired: 'Enter a postcode or ZIP code',
      },
    },
    signupEmail: {
      label: 'Work email address',
      personalWarning:
        'This looks like a personal email address. Use your work email if you have one.',
      sharedInboxWarning:
        'This looks like a shared inbox. Use your own work email if you have one, so the account is tied to you.',
    },
    userDetails: {
      title: 'Set up your personal account',
      implicitTerms:
        'By creating an account, you agree to our <a href="https://www.riskledger.com/terms" target="_blank">Terms of Service</a> and <a href="https://www.riskledger.com/privacy" target="_blank">Privacy Policy</a>.',
      loginDetails: 'Login details',
      notificationsTitle: 'Email notifications',
      notificationsBody:
        "We send notifications to keep you up to date with important events related to your organisation's security and assurance. We recommend receiving these alerts, but you can always turn them off in your settings later.",
      notificationsText: 'Receive notification emails',
      continue: 'Create account',
      miscFail: 'An unknown error occurred. Contact support for help.',
      validation: {
        emailRequired: 'Enter your email address',
        firstNameRequired: 'Enter your first name',
        lastNameRequired: 'Enter your last name',
        teamRequired: 'Select your team',
        mobileRequired: 'Enter your mobile number',
        passwordRequired: 'Enter a password',
        confirmPasswordRequired: 'Confirm your password',
      },
    },
    authFlowErrors: {
      emailExists:
        "We couldn't complete signup with those details. If you already have an account, please log in.",
      alreadyActivated:
        "We couldn't complete signup with those details. If you already have an account, please log in.",
      codeMismatch: "That code didn't match. Try again.",
      expired: 'That code has expired. Request a new one and try again.',
      passwordPolicy: "That password doesn't meet the requirements. Please try a different one.",
      deliveryFailed:
        "We couldn't send a verification email to that address. Please check it and try again.",
      rateLimit: 'Too many attempts. Wait a few minutes before trying again.',
      generic: 'Something went wrong. Please try again or contact support.',
    },
    confirmSignup: {
      body1:
        "To verify the email address that you've provided, we've emailed you a confirmation code. Please enter this below.",
      body2:
        "This can sometimes go through to your junk folder, but if you're having issues receiving this, reach out to {email} or {intercom} to open a chat.",
      body3: 'The verification code has been sent to {0}.',
      codeResent: "We've sent a new code. Enter the one from the newest email.",
      loginFailed: "Your email is verified, but we couldn't sign you in",
      codeRequired: 'Enter your verification code',
    },
    setupMFA: {
      title: 'Set up two factor authentication',
      subtitle: 'Two factor authentication (2FA) is mandatory for all users',
      appTitle: 'Authenticator app',
      appBody1:
        "We recommend using an authenticator app for 2FA as they are more secure and don't rely on mobile services.",
      appBody2:
        'Scan the QR code using your authenticator app or use the secret key to create an authentication code',
      enterCode: 'Enter authentication code',
      authenticationCodeRequired: 'Enter your authentication code',
      smsTitle: 'SMS authentication',
      smsBody: "Don't want to use an authenticator app?",
      smsCTA: 'Use SMS instead',
      smsNote:
        'SMS codes can only be sent to mobile numbers. If the phone number on your account is a landline, use an authenticator app instead.',
      scanCode: 'Scan the QR code with your authenticator app',
      copyKey: 'Or copy the secret key',
      verifySMS: {
        title: 'Verify your phone number',
        body: "We've sent a 6-digit code by SMS to {phone}. Enter it below to enable SMS authentication.",
        codeLabel: 'Verification code',
        codeRequired: 'Enter your verification code',
        cta: 'Verify and enable SMS',
        resend: 'Resend code',
        resent: 'A new code has been sent.',
        back: 'Back to authenticator app',
        errors: {
          codeMismatch: "That code didn't match. Try again.",
          expired: 'That code has expired. Use the Resend code button to get a new one.',
          rateLimited: 'Too many attempts. Wait a few minutes before trying again.',
          rateLimitedSend: 'Too many code requests. Wait a few minutes before requesting another.',
          sendFailed:
            "We couldn't send a code to your phone. Check that the number on file is correct or contact support.",
          enableFailed:
            "Your phone is verified, but we couldn't switch to SMS authentication. Try again or contact support.",
          generic:
            'Something went wrong verifying your phone number. Try again or contact support.',
        },
      },
    },
    orgSurvey: {
      title: "Let's learn about your organisation",
      subtitle: "We'll use this to tailor your experience",
      whoHandlesAssessments: 'Who typically handles security assessments at your organisation?',
      whoHandlesAssessmentsAnswer1: 'I handle them directly',
      whoHandlesAssessmentsAnswer2: 'Dedicated security/compliance team',
      whoHandlesAssessmentsAnswer3: 'Various team members',
      whoHandlesAssessmentsAnswer4: 'External consultant',
      howOften: 'How often does your organisation complete security assessments for clients?',
      howOftenAnswer1: 'This is our first time',
      howOftenAnswer2: 'Occasionally (1-2 times per year)',
      howOftenAnswer3: 'Regularly (3-10 times per year)',
      howOftenAnswer4: 'Frequently (10+ times per year)',
      howOftenAnswer5: "I don't know",
      bestDescription: 'What best describes your security program?',
      bestDescriptionAnswer1: 'Just getting started',
      bestDescriptionAnswer2: 'Basic controls in place',
      bestDescriptionAnswer3: 'Mature program with regular audits',
      bestDescriptionAnswer4: 'Advanced/certified (ISO 27001, SOC 2, etc.)',
      tools: 'Do you currently use any of these tools? Choose all that are relevant',
      toolsAnswer1: 'Security questionnaire tools',
      toolsAnswer2: 'GRC platforms',
      toolsAnswer3: 'Trust centres (Vanta, SafeBase, etc)',
      toolsAnswer4: 'None of the above',
    },
    selectOrganisation: {
      title: 'Select organisation',
      searchPlaceholder: 'Search',
      switchSearchPlaceholder: 'Search organisations...',
      organisations: 'Your organisations',
      results: 'Results',
      noMatches: 'No organisations match your search.',
      noOtherOrgs: 'You have no other organisations to switch to.',
      backToLogin: 'Back to login',
      errors: {
        orgUnavailable: 'That organisation is no longer available.',
        generic: 'Something went wrong selecting that organisation. Try again.',
      },
    },
    sessionChanged: {
      orgChangedTitle: 'Your active organisation has changed',
      orgChangedBody: 'Your active organisation has changed in another tab. Reload to continue.',
      reload: 'Reload',
    },
    cookieChallenge: {
      title: 'Enter your verification code',
      subtitle: 'This will be sent via an SMS message or found in your authenticator app',
      codeLabel: 'Enter 6 digit code',
      codeRequired: 'Enter your verification code',
      submit: 'Confirm',
      problems: 'Problems logging in? Contact us',
      unsupported: "MFA setup isn't available here — please use the standard login.",
      backToLogin: 'Back to login',
    },
  },
};
